// TimerWidget — the time-entry form, bound to a timer slot or unbound.
// One-click timers and the Timers panel (docs/specs/timers-one-click-spec.md,
// Track F, with the 2026-10-14 review-panel amendments).
//
// Where it renders:
//   • Browser: Layout.js's in-page DraggablePopup — standalone={false},
//     shortcutsEnabled while the popup is visible, onRebind={setInPageKey},
//     onClose hides the popup (the timer keeps running in its slot).
//   • Desktop: PopupTimer.js, as the whole content of a pop-out window
//     (standalone).  The desktop main window no longer renders an in-page
//     timer; it opens forms in pop-outs (lib/timerWindows.openTimerForm).
//
// THE SLOT IS THE CLOCK.  This form keeps no clock of its own — no interval
// or start-time refs.  Elapsed time, paused/ticking and every saved field are
// read from the slot through lib/timerStore.js (the ONE place slots are
// written), and every change goes back through it:
//   • clock: store.startNewTimer / toggle / startOrResume / pause — the
//     one-running rule (starting or resuming pauses my other timer) lives
//     there, not here;
//   • fields: setFields (debounced FIELD_MERGE_MS), setMatter / setAttorney
//     (immediate — they clear the phase and re-derive the billing status /
//     rate in the same write), setTypedHours;
//   • save: claimForSave → POST (client_ref = the slot's tid) or, for a
//     resume slot, saveResumedEntry (PUT with expected_updated_at) → then
//     finishSave, which removes the slot and broadcasts 'saved'.  Every
//     failure leaves the slot PAUSED with its exact seconds.
// The form re-derives from the slot on every write (this window's
// TIMER_SLOT_EVENT, other windows' storage events) but keeps every field it
// has an unflushed edit for (amendment 4), so typing is never clobbered.
//
// Unbound (no slot): an ordinary entry form.  START creates a slot from
// whatever is filled in — a matter is never needed to START — and binds to
// it.  Save with typed hours is the manual path: one POST, no slot.
//
// Props
//   storageKey        the slot to show ('lextrack_timer:…'), or null for an
//                     unbound form.  A key with no slot yet (PopupTimer's
//                     fresh key) is unbound, and START creates the slot there.
//                     Changing it flushes the previous slot's pending edits
//                     and rebinds; null shows the unbound, prefilled form.
//                     A key holding someone else's timer (row.mine false —
//                     another person signed in on this computer, or the token
//                     changed) is never bound, shown or written: the form is
//                     unbound and says so.
//   onRebind(key|null)  told whenever the form binds itself to another slot
//                     (START, Continue, a resume seed) or goes idle (saved,
//                     discarded, removed elsewhere).  Not called for a
//                     storageKey change the parent made.
//   resumeData        an entry row (GET /api/time-entries/:id) to continue:
//                     seeds the canonical 'lextrack_timer:resume:<id>' slot
//                     PAUSED (ran:false) — or restores a live one already
//                     there — and binds.  Pass a stable object: a new object
//                     is a new request.
//   initialMatterId, initialRate  prefill for the unbound form (a prefill is
//                     not a choice: rate_touched stays false).  While unbound,
//                     a change re-applies matter + rate.
//   standalone        a pop-out window: Discard/Done close the window, a save
//                     auto-closes it after 12 s (not after a failure).
//   popupKey          the popupKey this window was opened under, recorded in
//                     the slot's owner heartbeat so ↗ can focus this window.
//   shortcutsEnabled  Space (start/stop) and Ctrl/Cmd+Enter (save) — only the
//                     visible in-page popup turns them on.
//   onClose           in-page Minimize, and after Discard / Done.
//   store             injectable timerStore (tests).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api, { getMatters } from '../services/api'
import { ACTIVITY_CODES } from '../lib/activityCodes'
import { deriveDefaultBillingStatus, firmDefaultBillingStatus } from '../utils/billingDefaults'
import { useAuth } from '../context/AuthContext'
import { useAsk } from '../context/PromptProvider'
import NarrativeTemplatePicker, { NarrativeAutocomplete } from './NarrativeTemplatePicker'
import PhasePicker from './PhasePicker'
import {
  saveResumedEntry, saveResultMessage, postFailureText, postTimeEntry, postedMessage,
  billableHoursFromSeconds, needsPost, normalizeStartTime
} from '../lib/timeEntryPosting'
import { useTimerCommandListener, useTimerSlotHeartbeat } from '../lib/timerCommands'
import {
  entryAttorneyId, foreignPersonalMatter, foreignPersonalNote, timeEntryMatterChoices
} from '../lib/timeEntryMatterOptions'
import timerStore, {
  BILLING_STATUSES, MAX_ENTRY_HOURS, MESSAGES, TIMER_SLOT_EVENT, broadcastTimerEvent,
  buildPutBody, postUncertainty, replayDiff, saveConflictMessage, slotStartTime, tokenUid
} from '../lib/timerStore'
import { awaitHeldPauses } from '../lib/timerWindows'
import {
  NO_MATTER_LABEL, formatBarElapsed, isLegacySlotKey, isResumeSlotKey, isTimerSlotKey, newUuid
} from '../lib/timerSlots'
import { displayClientForMatterRow } from '../lib/displayClient'
import { hasPermission } from '../lib/navItems'
import { firmDateIso } from '../utils/eventTime'
import useFieldIds from '../hooks/useFieldIds'

// Field edits are merged into the slot this long after the last keystroke:
// every slot write wakes every window of the app.
export const FIELD_MERGE_MS = 400
// A merge refused because a save holds the slot is retried after this.
const CLAIM_RETRY_MS = 1500
export const AUTO_CLOSE_MS = 12000
// Refusals that keep an unflushed edit for a later flush (a save in flight,
// an old-code slot not adopted yet, a storage hiccup).
const KEEP_PENDING = new Set(['claimed', 'needs-adopt', 'needs-confirm', 'storage'])
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TOKEN_KEY = 'lextrack_token'
// Inputs a re-derive never rewrites while they have focus (a time input,
// like a date input, reads '' while a segment is half typed).
const FOCUS_GUARDED = ['hours', 'date', 'rate', 'start_time']

const formatElapsed = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0))
  const h = Math.floor(n / 3600)
  const m = Math.floor((n % 3600) / 60)
  const sec = n % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

// Hours (as typed or saved) → whole seconds, half-up.  A conversion, not a
// billing rule: billable hours always come from billableHoursFromSeconds.
const hoursToSeconds = (h) => {
  const n = Number(h)
  return Number.isFinite(n) && n > 0 ? Math.floor(n * 3600 + 0.5) : 0
}

const dateOnly = (v) => {
  if (typeof v !== 'string') return ''
  const d = v.slice(0, 10)
  return DATE_RE.test(d) ? d : ''
}

const str = (v) => (v == null ? '' : String(v))

// The Time field while paused: typed hours, else the billing value of the
// clock (rounded UP — a paused Save reads the slot, never this text).
function hoursShown (slot, elapsed) {
  const ht = Number(slot && slot.hours_typed)
  if (Number.isFinite(ht) && ht > 0) return String(ht)
  return elapsed > 0 ? billableHoursFromSeconds(elapsed).toFixed(1) : ''
}

function slotDateOf (slot) {
  const d = dateOnly(slot && slot.date)
  if (d) return d
  const at = Number(slot && slot.created_at) || Number(slot && slot.startTime) || Date.now()
  return firmDateIso(at)
}

// The form as the slot says it is.
function formFromRow (r) {
  const s = (r && r.slot) || {}
  const onBehalf = s.user_id != null && s.user_id !== '' && String(s.user_id) !== String(s.uid)
  return {
    matter_id: r.matterId || '',
    date: slotDateOf(s),
    hours: r.ticking ? '' : hoursShown(s, r.elapsed),
    rate: str(s.rate),
    description: str(s.description),
    billing_status: BILLING_STATUSES.includes(s.billing_status) ? s.billing_status : 'billable',
    activity_code: str(s.activity_code),
    user_id: onBehalf ? String(s.user_id) : '',
    matter_phase_id: s.matter_phase_id ?? null,
    // When the work began (firm-local): typed, the resumed entry's own, or
    // the timer's first start — lib/timerStore slotStartTime.
    start_time: slotStartTime(s)
  }
}

// What a re-derive depends on (the elapsed seconds only while not ticking —
// the Time field shows them then; a ticking clock re-renders, not re-derives).
const deriveSig = (r) => {
  try { return JSON.stringify([r.slot, r.ticking, r.ticking ? 0 : r.elapsed]) } catch { return String(Math.random()) }
}

function readRow (store, k) {
  if (!k || !isTimerSlotKey(k)) return null
  try { return store.getTimer(k) } catch { return null }
}

// Someone else's timer — another person signed in on this computer, or the
// token changed under an open form.  The form never binds to it, never shows
// its fields (matter, notes, time) and never writes it (the firm decision of
// 2026-10-14: other users on the computer never see my timers).
const isForeignRow = (r) => !!r && r.mine === false
const foreignMessage = () => (tokenUid() == null ? MESSAGES.noUser : MESSAGES.foreign)

// Two hours values that bill the same (the 0.1-hour grid).
const sameTenth = (a, b) => Math.round(Number(a) * 10) === Math.round(Number(b) * 10)

const errorText = (err, fallback) => err?.response?.data?.error || fallback

export default function TimerWidget ({
  storageKey = null,
  initialMatterId = '',
  initialRate = '',
  resumeData = null,
  standalone = false,
  popupKey = null,
  shortcutsEnabled = false,
  onRebind,
  onClose,
  store = timerStore
}) {
  const fid = useFieldIds()
  const ask = useAsk()
  const { user } = useAuth()
  const canViewAll = hasPermission(user, 'can_view_all_time')

  // ── props the event handlers read (they are registered once) ──────────
  const propsRef = useRef(null)
  propsRef.current = { initialMatterId, initialRate, onRebind, onClose, standalone }

  const blankForm = (prefill = true) => {
    const p = propsRef.current
    const matterId = prefill ? str(p.initialMatterId) : ''
    return {
      matter_id: matterId,
      date: firmDateIso(),
      hours: '',
      rate: prefill ? str(p.initialRate) : '',
      description: '',
      billing_status: firmDefaultBillingStatus(),
      activity_code: '',
      user_id: '',
      matter_phase_id: null,
      // No timer has started: blank until START (the first start fills it)
      // or the person types one.
      start_time: ''
    }
  }

  // ── binding ───────────────────────────────────────────────────────────
  const [initial] = useState(() => {
    const k = isTimerSlotKey(storageKey) ? storageKey : null
    const r = readRow(store, k)
    // Someone else's timer: an unbound form that says so (START mints a
    // fresh key of mine; nothing of theirs is shown or written).
    if (isForeignRow(r)) return { k: null, r: null, refused: foreignMessage() }
    return { k, r, refused: '' }
  })
  const keyRef = useRef(initial.k)
  const [key, setKey] = useState(initial.k)
  const rowRef = useRef(initial.r)
  const [row, setRow] = useState(initial.r)
  // Did the bound key hold a slot (so its disappearance means "saved or
  // discarded somewhere else", not "not started yet")?
  const hadSlotRef = useRef(!!initial.r)
  // Our own save / discard is removing this key: not "removed elsewhere".
  const expectRemovalRef = useRef(null)
  const deriveSigRef = useRef(initial.r ? deriveSig(initial.r) : null)
  const lastPropKeyRef = useRef(storageKey)

  const [form, setForm] = useState(() => (initial.r ? formFromRow(initial.r) : blankForm()))
  const formRef = useRef(form)
  formRef.current = form
  // Unflushed field edits for the bound slot: { field: value }.  A foreign
  // write never overwrites these (amendment 4); a save flushes them first.
  const pendingRef = useRef({})
  const flushTimerRef = useRef(null)
  // The unbound form's own choices (a prefill or the firm default is not a
  // choice — startNewTimer persists them as billing_touched / rate_touched).
  const billingTouchedRef = useRef(false)
  const rateTouchedRef = useRef(false)
  const dateTouchedRef = useRef(false)
  const startTimeTouchedRef = useRef(false)
  const userTypedHoursRef = useRef(false)
  // client_ref for the manual (unbound) path: a retried save after a lost
  // response is replayed by the server instead of duplicated.
  const manualRefRef = useRef(newUuid())

  const [error, setError] = useState(initial.refused)
  const [success, setSuccess] = useState('')
  const [notice, setNotice] = useState('')
  const [storageFailed, setStorageFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const actingRef = useRef(false)
  const [justSavedEntry, setJustSavedEntry] = useState(null)
  const [postingJustSaved, setPostingJustSaved] = useState(false)
  const autoCloseRef = useRef(null)
  const successTimerRef = useRef(null)
  const heldCancelRef = useRef(null)
  const mountedRef = useRef(true)

  const [matters, setMatters] = useState([])
  const [teamMembers, setTeamMembers] = useState([])
  const [rateHint, setRateHint] = useState(null)
  // Bumped when the firm's default billing status arrives (/api/settings in
  // a pop-out, Layout's 'lextrack-defaults-loaded' in the main window).
  const [defaultsVersion, setDefaultsVersion] = useState(0)

  const confirm = useCallback(
    (req) => ask({ type: 'confirm', danger: true, message: req.message, confirmLabel: req.confirmLabel }),
    [ask]
  )

  const clearAutoClose = () => {
    if (autoCloseRef.current) { clearTimeout(autoCloseRef.current); autoCloseRef.current = null }
  }

  const resetUnboundChoices = () => {
    billingTouchedRef.current = false
    rateTouchedRef.current = false
    dateTouchedRef.current = false
    startTimeTouchedRef.current = false
    userTypedHoursRef.current = false
  }

  // A store refusal → what the person sees.  'cancelled' (they said no to a
  // confirm) shows nothing; 'storage' shows the persistent banner.
  const showRefusal = (res) => {
    if (!res || res.ok) return
    if (res.reason === 'storage') { setStorageFailed(true); return }
    if (res.reason === 'cancelled') return
    setError(res.message || MESSAGES.invalid)
  }

  // ── reading the slot ─────────────────────────────────────────────────
  // Re-read `k` and re-derive the form from it, keeping unflushed edits.
  const applyRow = (k, r, force = false) => {
    if (k !== keyRef.current) return
    if (isForeignRow(r)) {
      // The token changed under this form (or the slot was replaced by
      // someone else's): unbind without flushing — never derive from it,
      // never write it — and say why.
      goIdle()
      setError(foreignMessage())
      return
    }
    if (!r) {
      const gone = rowRef.current
      rowRef.current = null
      setRow(null)
      if (!hadSlotRef.current) return
      hadSlotRef.current = false
      deriveSigRef.current = null
      if (expectRemovalRef.current === k) { expectRemovalRef.current = null; return }
      // Saved, discarded or cleared somewhere else: go idle, never resurrect.
      // The store's removal note says when it went for a reason of its own
      // (matter.blocked removed this resume timer).
      let note = null
      try {
        note = typeof store.removalNote === 'function'
          ? store.removalNote(k, { tid: gone && gone.slot ? gone.slot.tid : undefined })
          : null
      } catch { note = null }
      goIdle({ message: (note && note.message) || MESSAGES.missing })
      return
    }
    hadSlotRef.current = true
    rowRef.current = r
    setRow(r)
    const sig = deriveSig(r)
    if (!force && sig === deriveSigRef.current) return
    deriveSigRef.current = sig
    const next = formFromRow(r)
    // Keep every field with an unflushed edit, and the input being typed in:
    // a number or date input reads '' mid-entry ("1." / a cleared segment),
    // and re-deriving it would overwrite what the person is typing.
    const keep = new Set(Object.keys(pendingRef.current))
    const focused = focusedField()
    if (focused) keep.add(focused)
    setForm(prev => {
      const out = { ...next }
      for (const name of keep) {
        if (name in prev) out[name] = prev[name]
      }
      return out
    })
  }

  // Which focus-guarded input has focus, if any.
  function focusedField () {
    try {
      const el = typeof document !== 'undefined' ? document.activeElement : null
      if (!el || !el.id) return null
      return FOCUS_GUARDED.find(name => fid(name) === el.id) || null
    } catch { return null }
  }

  // Leaving a guarded input: merge it now and show what the slot holds.
  const onGuardedBlur = () => {
    setTimeout(() => {
      if (!mountedRef.current) return
      flushRef.current()
      refreshRef.current(true)
    }, 0)
  }

  const refresh = (force = false) => {
    if (!mountedRef.current) return
    const k = keyRef.current
    if (!k) return
    applyRow(k, readRow(store, k), force)
  }
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  // ── flushing field edits into the slot ───────────────────────────────
  const flush = () => {
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null }
    const k = keyRef.current
    const pending = pendingRef.current
    if (!k || Object.keys(pending).length === 0) return { ok: true, changed: false }
    pendingRef.current = {}
    const { hours, ...fields } = pending
    let result = { ok: true, changed: false }
    let keep = {}
    let left = fields
    // setFields is all-or-nothing: drop a field it calls invalid (a cleared
    // date, a negative rate) and merge the rest; the form then shows the
    // slot's value for the dropped one.
    for (let i = 0; i < 10 && Object.keys(left).length > 0; i++) {
      const r = store.setFields(k, left)
      if (r.ok) { result = r; break }
      if (r.reason === 'invalid' && r.field && r.field in left) {
        const { [r.field]: _dropped, ...rest } = left
        left = rest
        continue
      }
      result = r
      if (KEEP_PENDING.has(r.reason)) keep = { ...keep, ...left }
      break
    }
    if (hours !== undefined) {
      if (result.ok) {
        const r = store.setTypedHours(k, hours)
        if (!r.ok) {
          if (KEEP_PENDING.has(r.reason)) keep.hours = hours
          if (r.reason !== 'ticking') result = r
        }
      } else if (KEEP_PENDING.has(result.reason)) {
        keep.hours = hours
      }
    }
    if (Object.keys(keep).length > 0) {
      pendingRef.current = { ...keep, ...pendingRef.current }
      if (result.reason === 'claimed') scheduleFlush(CLAIM_RETRY_MS)
    }
    if (!result.ok && result.reason === 'storage') setStorageFailed(true)
    refreshRef.current(true)
    return result
  }
  const flushRef = useRef(flush)
  flushRef.current = flush

  function scheduleFlush (ms = FIELD_MERGE_MS) {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null
      flushRef.current()
    }, ms)
  }

  // ── binding transitions ──────────────────────────────────────────────
  function unbindState () {
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null }
    pendingRef.current = {}
    keyRef.current = null
    setKey(null)
    rowRef.current = null
    setRow(null)
    hadSlotRef.current = false
    deriveSigRef.current = null
    expectRemovalRef.current = null
  }

  // The bound slot is gone (or the person discarded / the parent unbound):
  // an unbound form again, prefilled as the parent asked.
  function goIdle ({ message = '', notify = true } = {}) {
    unbindState()
    resetUnboundChoices()
    manualRefRef.current = newUuid()
    setForm(blankForm())
    if (message) setNotice(message)
    if (notify && typeof propsRef.current.onRebind === 'function') propsRef.current.onRebind(null)
  }

  // → false (nothing changed) when `k` holds someone else's timer.
  function bind (k, { notify = true } = {}) {
    if (isForeignRow(readRow(store, k))) { setError(foreignMessage()); return false }
    if (keyRef.current && keyRef.current !== k) flushRef.current()
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null }
    pendingRef.current = {}
    keyRef.current = k
    setKey(k)
    hadSlotRef.current = false
    deriveSigRef.current = null
    expectRemovalRef.current = null
    const r = readRow(store, k)
    if (r) applyRow(k, r, true)
    else {
      rowRef.current = null
      setRow(null)
      resetUnboundChoices()
      setForm(blankForm())
    }
    if (notify && typeof propsRef.current.onRebind === 'function') propsRef.current.onRebind(k)
    return true
  }

  // The parent rebinding us (Layout's open-time-entry-popup unbinds; Fill in
  // & save binds): flush the previous slot first, never retarget it.
  useEffect(() => {
    if (storageKey === lastPropKeyRef.current) return
    lastPropKeyRef.current = storageKey
    const k = isTimerSlotKey(storageKey) ? storageKey : null
    if (k === keyRef.current) return
    if (keyRef.current) flushRef.current()
    setError('')
    setNotice('')
    // Someone else's timer: an unbound form saying so, never theirs.
    if (k && bind(k, { notify: false })) return
    goIdle({ notify: false })
    if (k) setError(foreignMessage())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  // A new prefill while unbound re-applies matter + rate (not a choice).
  const prefillRef = useRef({ m: initialMatterId, r: initialRate })
  useEffect(() => {
    const prev = prefillRef.current
    if (prev.m === initialMatterId && prev.r === initialRate) return
    prefillRef.current = { m: initialMatterId, r: initialRate }
    if (keyRef.current && rowRef.current) return
    rateTouchedRef.current = false
    setForm(f => ({ ...f, matter_id: str(initialMatterId), rate: str(initialRate), matter_phase_id: null }))
  }, [initialMatterId, initialRate])

  // ── subscriptions: this window's writes, other windows', the token ───
  useEffect(() => {
    mountedRef.current = true
    const onLocal = () => refreshRef.current()
    const onStorage = (e) => {
      const k = e ? e.key : undefined
      if (k === null || k === TOKEN_KEY || isTimerSlotKey(k)) refreshRef.current()
    }
    window.addEventListener(TIMER_SLOT_EVENT, onLocal)
    window.addEventListener('storage', onStorage)
    refreshRef.current(true)
    return () => {
      window.removeEventListener(TIMER_SLOT_EVENT, onLocal)
      window.removeEventListener('storage', onStorage)
      // Flush pending merges on unmount (graft 4) — the timer lives on.
      flushRef.current()
      mountedRef.current = false
      if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null }
      if (autoCloseRef.current) { clearTimeout(autoCloseRef.current); autoCloseRef.current = null }
      if (successTimerRef.current) { clearTimeout(successTimerRef.current); successTimerRef.current = null }
      if (heldCancelRef.current) { heldCancelRef.current(); heldCancelRef.current = null }
    }
  }, [])

  const bound = !!(key && row)
  const isTicking = bound && row.ticking
  const isPaused = bound && !row.ticking
  const resumeEntryId = bound && row.isResume ? row.resumeEntryId : null
  const postUncertain = bound && row.postUncertain
  const matterLocked = !!resumeEntryId || postUncertain

  // The clock face: re-read once a second, only while ticking.
  useEffect(() => {
    if (!isTicking) return undefined
    const id = setInterval(() => refreshRef.current(), 1000)
    return () => clearInterval(id)
  }, [isTicking])

  // ── resumeData: seed (or restore) the canonical resume slot ──────────
  const seededRef = useRef(null)
  useEffect(() => {
    if (!resumeData || resumeData.id == null || seededRef.current === resumeData) return
    seededRef.current = resumeData
    const res = store.seedResume(resumeData.id, {
      entry: resumeData,
      seedSeconds: hoursToSeconds(resumeData.hours),
      entryUpdatedAt: resumeData.updated_at ?? null
    })
    if (!res.ok) { showRefusal(res); return }
    if (res.key !== keyRef.current) bind(res.key, { notify: res.key !== lastPropKeyRef.current })
    else refreshRef.current(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeData])

  // ── reference data ───────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    getMatters({ slim: true }).then(res => {
      if (cancelled) return
      const list = Array.isArray(res?.data) ? res.data : []
      setMatters(list.slice().sort((a, b) => str(a.title).localeCompare(str(b.title), undefined, { sensitivity: 'base' })))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!canViewAll) return undefined
    let cancelled = false
    api.get('/api/team').then(r => {
      if (!cancelled) setTeamMembers((Array.isArray(r?.data) ? r.data : []).filter(u => u.is_active))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [canViewAll])

  // Pop-outs have no Layout: fetch the firm default ourselves.  It fills in
  // only an unbound form with no matter and no explicit pick (feedback #741);
  // a bound slot keeps what it was started with.
  useEffect(() => {
    const applyDefault = () => {
      // An unbound prefilled matter whose billing type defers to the firm
      // default re-derives with the loaded value (effect below).
      setDefaultsVersion(v => v + 1)
      if (keyRef.current && rowRef.current) return
      setForm(f => (billingTouchedRef.current || f.matter_id) ? f : { ...f, billing_status: firmDefaultBillingStatus() })
    }
    if (!window.__lextrackDefaultBillingStatus) {
      api.get('/api/settings').then(r => {
        window.__lextrackDefaultBillingStatus = r?.data?.default_time_entry_billing_status || 'billable'
        applyDefault()
      }).catch(() => { window.__lextrackDefaultBillingStatus = 'billable' })
    }
    window.addEventListener('lextrack-defaults-loaded', applyDefault)
    return () => window.removeEventListener('lextrack-defaults-loaded', applyDefault)
  }, [])

  // The rate the server will use when Rate is left blank (a hint, never
  // written into the field: the server's getEffectiveRate decides at save).
  useEffect(() => {
    const mid = form.matter_id
    if (!mid) { setRateHint(null); return undefined }
    let cancelled = false
    const q = new URLSearchParams({ matter_id: mid })
    if (form.user_id) q.set('user_id', form.user_id)
    if (DATE_RE.test(form.date || '')) q.set('date', form.date)
    api.get(`/api/time-entries/effective-rate?${q.toString()}`).then(r => {
      if (!cancelled) setRateHint(r?.data?.rate != null && Number.isFinite(Number(r.data.rate)) ? Number(r.data.rate) : null)
    }).catch(() => { if (!cancelled) setRateHint(null) })
    return () => { cancelled = true }
  }, [form.matter_id, form.user_id, form.date])

  // A slot started from a prefill before the matter list arrived has an id
  // but no title ("No matter yet" in the panel) and a billing status derived
  // without the matter's billing type (the firm default, e.g. no-charge-show,
  // for an hourly matter).  Once the matter is known: fill the title in and,
  // unless the person picked a status, re-derive it as setMatter would — but
  // only for a billing type that decides the status by itself (hourly →
  // billable, contingency → no-charge-show).  Every other type falls back to
  // the firm default, which the slot already holds from when it started; a
  // pop-out that has not loaded /api/settings yet would read 'billable' and
  // overwrite a correct no-charge-show (the revenue trap, reversed).
  const rowMatterId = row ? row.matterId : ''
  const rowMatterTitle = row ? row.matterTitle : ''
  useEffect(() => {
    const r = rowRef.current
    const k = keyRef.current
    if (!k || !r || r.cls !== 'v2' || r.isResume || r.claimed || r.postUncertain || !r.matterId) return
    const m = matters.find(x => String(x.id) === r.matterId)
    if (!m) return
    if (!r.matterTitle && m.title) store.setMatter(k, { matter_id: r.matterId, matter_title: m.title, billing_type: m.billing_type })
    const bt = str(m.billing_type).toLowerCase()
    if (bt !== 'hourly' && bt !== 'contingency') return
    const slot = store.readSlot(k)
    if (!slot || slot.billing_touched) return
    const derived = deriveDefaultBillingStatus(bt)
    if (derived !== slot.billing_status) store.setFields(k, { billing_status: derived, billing_touched: false })
  }, [matters, rowMatterId, rowMatterTitle, store])

  // The unbound form's matter (a prefill) gets its billing status from the
  // matter's billing type once the list arrives — and again once the firm
  // default arrives, for a type that defers to it — unless the person picked
  // one.  Only the form changes here; START derives the slot's own status.
  useEffect(() => {
    if ((keyRef.current && rowRef.current) || billingTouchedRef.current) return
    setForm(f => {
      if (!f.matter_id) return f
      const m = matters.find(x => String(x.id) === String(f.matter_id))
      if (!m) return f
      const derived = deriveDefaultBillingStatus(m.billing_type)
      return derived === f.billing_status ? f : { ...f, billing_status: derived }
    })
  }, [matters, initialMatterId, defaultsVersion])

  const matterById = (id) => matters.find(m => String(m.id) === String(id))

  // ── the owner heartbeat + commands (old bar, ↗ focus) ────────────────
  // Not while a window from before the update holds the slot (v1-held): our
  // {v:2} record would overwrite its v-less one, re-classify the slot as
  // adoptable and let a save or toggle here write a clock that window still
  // keeps in React refs.  Nor would we listen: its commands are for it, and
  // store.pause/startOrResume on a held slot SEND the same command — which
  // this window's own listener would hear again, without end.
  const heldElsewhereOld = bound && row.cls === 'v1-held'
  const presence = !!key && !heldElsewhereOld
  // Declared before the listener, whose callback acknowledges through it.
  const heartbeat = useTimerSlotHeartbeat(key, {
    enabled: presence, popupKey, active: isTicking, by: standalone ? 'popout' : 'inpage'
  })

  const handleHeld = (held) => {
    if (!Array.isArray(held) || held.length === 0) return
    if (heldCancelRef.current) heldCancelRef.current()
    heldCancelRef.current = awaitHeldPauses(held, {
      onSilence: ({ message }) => { if (mountedRef.current) setNotice(message) }
    })
  }

  // A clock transition's result.
  const afterClock = (res) => {
    if (!res) return false
    if (!res.ok) { showRefusal(res); refreshRef.current(true); return false }
    setStorageFailed(false)
    if (Array.isArray(res.failed) && res.failed.length > 0) setStorageFailed(true)
    handleHeld(res.held)
    refreshRef.current(true)
    return true
  }

  const commandsRef = useRef(null)
  commandsRef.current = {
    // Applied at cmd.at (clamped by the store); idempotent, so a duplicate
    // or a double-click on the bar applies once.
    pause: (cmd) => {
      const k = keyRef.current
      if (!k) return
      const res = store.pause(k, { at: cmd.at })
      if (!res.ok && res.reason === 'needs-adopt') { setError(MESSAGES.needsAdopt); return }
      afterClock(res)
    },
    resume: (cmd) => {
      const k = keyRef.current
      if (!k) return
      flushRef.current()
      Promise.resolve(store.startOrResume(k, { at: cmd.at, confirm })).then(res => {
        if (mountedRef.current) afterClock(res)
      }).catch(() => {})
    },
    // ↗ for a window whose popupKey the sender does not know.  Named in full
    // (not through an alias) so desktop/timerBar.test.js's preload-contract
    // scan sees it.
    focus: () => {
      try {
        if (typeof window.electronAPI?.focusSelf === 'function') { window.electronAPI.focusSelf(); return }
      } catch {}
      try { window.focus() } catch {}
    }
  }
  useTimerCommandListener(key, (cmd) => {
    const run = commandsRef.current && commandsRef.current[cmd.action]
    if (typeof run === 'function') run(cmd)
    // Heard — even when it did not apply — so the sender can tell "nothing
    // to do" from "this window is not listening".
    heartbeat.ack(cmd.id)
  }, { enabled: presence })

  // ── field edits ──────────────────────────────────────────────────────
  const editField = (name, value) => {
    setForm(f => ({ ...f, [name]: value }))
    if (keyRef.current && rowRef.current) {
      pendingRef.current = { ...pendingRef.current, [name]: value }
      scheduleFlush()
    }
  }

  // Adopt an old-code (v1) slot on an explicit action: silently where no old
  // holder can exist, else after the person confirms (timerStore.adoptV1).
  const ensureAdopted = async (k) => {
    const c = store.classify(k)
    if (!c || c.cls === 'v2') return { ok: true }
    if (c.cls === 'v1-held') return { ok: false, reason: 'held', message: MESSAGES.held }
    const a = await store.adoptV1(k, { confirm })
    if (a.ok) flushRef.current()
    return a
  }

  const onMatterChange = async (newId) => {
    setError('')
    const picked = matterById(newId)
    const k = keyRef.current
    if (!k || !rowRef.current) {
      setForm(f => {
        const next = { ...f, matter_id: newId, matter_phase_id: null }
        if (!billingTouchedRef.current) next.billing_status = deriveDefaultBillingStatus(newId ? picked?.billing_type : null)
        if (!rateTouchedRef.current) next.rate = ''
        return next
      })
      return
    }
    flushRef.current()
    const matter = { matter_id: newId, matter_title: picked?.title || '', billing_type: picked?.billing_type }
    let res = store.setMatter(k, matter)
    if (!res.ok && res.reason === 'needs-adopt') {
      const a = await ensureAdopted(k)
      res = a.ok ? store.setMatter(k, matter) : a
    }
    showRefusal(res)
    refreshRef.current(true)
  }

  const onAttorneyChange = async (newUserId) => {
    setError('')
    const k = keyRef.current
    if (!k || !rowRef.current) {
      setForm(f => ({ ...f, user_id: newUserId, rate: rateTouchedRef.current ? f.rate : '' }))
      return
    }
    flushRef.current()
    let res = store.setAttorney(k, newUserId)
    if (!res.ok && res.reason === 'needs-adopt') {
      const a = await ensureAdopted(k)
      res = a.ok ? store.setAttorney(k, newUserId) : a
    }
    showRefusal(res)
    refreshRef.current(true)
  }

  const onHoursChange = (val) => {
    if (keyRef.current && rowRef.current) { editField('hours', val); return }
    userTypedHoursRef.current = true
    setForm(f => ({ ...f, hours: val }))
  }

  const onBillingChange = (val) => {
    if (!(keyRef.current && rowRef.current)) billingTouchedRef.current = true
    editField('billing_status', val)
  }

  const onRateChange = (val) => {
    if (!(keyRef.current && rowRef.current)) rateTouchedRef.current = val !== ''
    editField('rate', val)
  }

  const onDateChange = (val) => {
    if (!(keyRef.current && rowRef.current)) dateTouchedRef.current = true
    editField('date', val)
  }

  // '' (cleared, or a time input mid-entry) is a choice too: blank is allowed.
  const onStartTimeChange = (val) => {
    if (!(keyRef.current && rowRef.current)) startTimeTouchedRef.current = true
    editField('start_time', val)
  }

  // ── the clock button ─────────────────────────────────────────────────
  const handleStart = async () => {
    const f = formRef.current
    const typed = userTypedHoursRef.current ? Number(f.hours) : NaN
    const seedSeconds = Number.isFinite(typed) && typed > 0 ? hoursToSeconds(typed) : 0
    const picked = matterById(f.matter_id)
    const fields = {
      matter_id: f.matter_id,
      matter_title: picked?.title || '',
      billing_type: picked?.billing_type,
      description: f.description,
      activity_code: f.activity_code,
      rate: f.rate,
      rate_touched: rateTouchedRef.current,
      user_id: f.user_id,
      matter_phase_id: f.matter_phase_id
    }
    if (billingTouchedRef.current) { fields.billing_status = f.billing_status; fields.billing_touched = true }
    if (dateTouchedRef.current) fields.date = f.date
    // A start time typed before START wins; otherwise the slot's first start.
    if (startTimeTouchedRef.current) { fields.start_time = f.start_time; fields.start_time_touched = true }
    // START on the key the parent gave (PopupTimer's fresh key) keeps the
    // window and its slot paired.  A key that held a timer in this form is
    // never reused (bind/goIdle drop it once its slot is gone), the legacy
    // and resume keys are never minted here, and a key holding anything at
    // all is refused by the store — otherwise the store mints a fresh key.
    const k = keyRef.current
    const startKey = k && !hadSlotRef.current && !isLegacySlotKey(k) && !isResumeSlotKey(k) &&
      !readRow(store, k) ? k : undefined
    const res = await store.startNewTimer({ fields, seedSeconds, key: startKey, confirm })
    if (!mountedRef.current) return
    if (!res.ok) { showRefusal(res); return }
    setStorageFailed(false)
    setJustSavedEntry(null)
    setSuccess('')
    userTypedHoursRef.current = false
    bind(res.key, { notify: res.key !== lastPropKeyRef.current })
    if (Array.isArray(res.failed) && res.failed.length > 0) setStorageFailed(true)
    handleHeld(res.held)
  }

  const handleTimerToggle = async () => {
    if (actingRef.current) return
    actingRef.current = true
    clearAutoClose()
    setError('')
    setNotice('')
    try {
      const k = keyRef.current
      if (!k || !rowRef.current) { await handleStart(); return }
      flushRef.current()
      const res = await store.toggle(k, { confirm })
      if (mountedRef.current) afterClock(res)
    } finally {
      actingRef.current = false
    }
  }

  // ── saving ───────────────────────────────────────────────────────────
  const closeWindow = () => {
    if (propsRef.current.standalone) {
      try { window.close() } catch {}
      return
    }
    if (typeof propsRef.current.onClose === 'function') propsRef.current.onClose()
  }

  const afterSaved = ({ saved, willPost, isResume, hours, seconds, snapshot, keepMatterId, keepRate }) => {
    setSuccess(saveResultMessage({ isResume, willPost, saved }))
    setError(saved?.postError ? postFailureText(saved.postError) : '')
    setNotice('')
    resetUnboundChoices()
    rateTouchedRef.current = !!keepRate
    manualRefRef.current = newUuid()
    // Matter (and a rate the person typed) stay for the common "next entry
    // on the same matter" flow — unless it is someone else's Personal matter
    // (an on-behalf save): the next entry is mine (user_id resets below), so
    // it starts with no matter instead of being cleared with a note.
    const keepId = foreignPersonalMatter(matters, keepMatterId, entryAttorneyId('', user?.id)) ? '' : keepMatterId
    const picked = matterById(keepId)
    setForm({
      matter_id: str(keepId),
      date: firmDateIso(),
      hours: '',
      rate: keepRate ? str(keepRate) : '',
      description: '',
      billing_status: deriveDefaultBillingStatus(keepId ? picked?.billing_type : null),
      activity_code: '',
      user_id: '',
      matter_phase_id: null,
      start_time: ''
    })
    if (saved?.id != null) {
      setJustSavedEntry({
        id: saved.id,
        hours: String(hours),
        actualSeconds: seconds,
        ...snapshot,
        wasResume: isResume,
        // Feedback #833 — drives the banner's Post button.  A brand-new
        // Save Entry is unposted even if the API did not echo status.
        status: saved.status || (willPost ? null : 'unposted'),
        updated_at: saved.updated_at ?? null
      })
    }
    // Pop-outs close 12 s after a save (Continue / Done / Discard / START
    // cancel it), never after a failure — a failed post stays to be read.
    if (propsRef.current.standalone && !saved?.postError) {
      clearAutoClose()
      autoCloseRef.current = setTimeout(() => { autoCloseRef.current = null; closeWindow() }, AUTO_CLOSE_MS)
    } else if (!saved?.postError) {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
      successTimerRef.current = setTimeout(() => { successTimerRef.current = null; if (mountedRef.current) setSuccess('') }, 3000)
    }
  }

  // A replayed POST (amendment 7): the server already holds this timer's
  // entry.  Same fields → saved (posting it if Save & Post asked and it is
  // still unposted).  Any whitelisted field differs → the slot becomes a
  // resume slot for that entry (markReplayed) and the total goes in with
  // saveResumedEntry — a lost response is neither duplicated nor
  // short-changed.
  const reconcileReplay = async ({ slot, row: entry, hours, willPost, mark }) => {
    const diff = replayDiff(slot, entry, { hours })
    if (!diff.differs) {
      let status = entry.status ?? null
      let reviewStatus = entry.review_status ?? null
      let justPosted = false
      let postError = null
      if (willPost && needsPost(status)) {
        try {
          const posted = await postTimeEntry(api, entry.id)
          status = posted.status
          reviewStatus = posted.review_status
          justPosted = true
        } catch (err) {
          postError = errorText(err, 'The entry could not be posted.')
        }
      }
      return { id: entry.id, status, review_status: reviewStatus, justPosted, postError, updated_at: entry.updated_at ?? null }
    }
    const marked = typeof mark === 'function' ? mark(entry) : null
    const resumeEntryId = entry.id
    const putSlot = (marked && marked.ok && marked.slot) || slot
    return saveResumedEntry(api, resumeEntryId, buildPutBody(putSlot), String(hours), {
      post: willPost, expectedUpdatedAt: entry.updated_at ?? null
    })
  }

  // The line for a failed save.
  const saveFailureMessage = (err, isResume) => {
    const status = err?.response?.status
    const data = err?.response?.data
    if (err?.code === 'entry_changed') return MESSAGES.entryChanged
    if (status === 409 && !isResume) return saveConflictMessage(data)
    if (status === 422 && data?.code === 'period_locked') return MESSAGES.periodClosed
    if (!isResume && postUncertainty(err) === true) return MESSAGES.postUncertain
    return errorText(err, 'Error saving time entry')
  }

  // The slot path: claim → POST / PUT → finish, or release and stay paused.
  const sendClaim = async (k, claim, willPost) => {
    const resumeEntryId = claim.isResume ? claim.entryId : null
    let saved
    let isResume = !!resumeEntryId
    try {
      if (resumeEntryId) {
        saved = await saveResumedEntry(api, resumeEntryId, claim.body, String(claim.hours), {
          post: willPost, expectedUpdatedAt: claim.expectedUpdatedAt
        })
      } else {
        const res = await api.post('/api/time-entries', { ...claim.body, post_immediately: willPost })
        const created = res?.data || {}
        if (created.replayed === true) {
          saved = await reconcileReplay({
            slot: claim.slot,
            row: created,
            hours: claim.hours,
            willPost,
            mark: (entry) => {
              isResume = true
              const m = store.markReplayed(k, claim.claimId, { entryId: entry.id, entryUpdatedAt: entry.updated_at ?? null })
              if (!m.ok && m.reason === 'storage') setStorageFailed(true)
              return m
            }
          })
        } else {
          saved = {
            id: created.id ?? created.entry?.id ?? null,
            status: created.status,
            review_status: created.review_status,
            updated_at: created.updated_at ?? null
          }
        }
      }
    } catch (err) {
      // Every failure: release the claim, the slot stays PAUSED with its
      // exact seconds.  A POST with no definitive answer stamps
      // post_uncertain (matter + attorney lock until a retry answers).
      const rel = store.releaseClaim(k, claim.claimId, isResume ? {} : { error: err })
      if (!rel.ok && rel.reason === 'storage') setStorageFailed(true)
      if (mountedRef.current) {
        setError(saveFailureMessage(err, isResume))
        refreshRef.current(true)
      }
      return
    }
    // Saved.  Remove the slot (finishSave also broadcasts 'saved').
    const slot = claim.slot || {}
    expectRemovalRef.current = k
    const fin = store.finishSave(k, claim.claimId, { tid: slot.tid })
    if (!fin.ok) {
      if (fin.reason === 'storage') setStorageFailed(true)
      broadcastTimerEvent('saved')
    }
    if (!mountedRef.current) return
    unbindState()
    if (typeof propsRef.current.onRebind === 'function') propsRef.current.onRebind(null)
    afterSaved({
      saved,
      willPost,
      isResume,
      hours: claim.hours,
      seconds: claim.seconds,
      keepMatterId: str(slot.matter_id),
      keepRate: slot.rate_touched ? str(slot.rate) : '',
      snapshot: {
        matter_id: str(slot.matter_id),
        matter_title: str(slot.matter_title),
        description: str(slot.description),
        activity_code: str(slot.activity_code),
        billing_status: slot.billing_status,
        rate: str(slot.rate),
        user_id: slot.user_id != null && String(slot.user_id) !== String(slot.uid) ? str(slot.user_id) : '',
        matter_phase_id: slot.matter_phase_id ?? null,
        date: dateOnly(claim.body?.date) || slotDateOf(slot),
        // What the entry now holds ("Continue this entry" seeds from it).
        start_time: slotStartTime(slot)
      }
    })
  }

  // The manual path: an unbound form with typed hours — one POST, no slot.
  const saveManual = async (willPost) => {
    const f = formRef.current
    if (!f.matter_id) { setError(MESSAGES.noMatter); return }
    if (!str(f.description).trim()) { setError(MESSAGES.noDescription); return }
    const seconds = hoursToSeconds(f.hours)
    if (seconds <= 0) { setError(MESSAGES.noTime); return }
    const hours = billableHoursFromSeconds(seconds)
    if (hours > MAX_ENTRY_HOURS) { setError(MESSAGES.tooLongHours(hours)); return }
    const date = dateOnly(f.date) || firmDateIso()
    const startTime = normalizeStartTime(f.start_time) || ''
    const picked = matterById(f.matter_id)
    const pseudo = {
      paused: true,
      matter_id: f.matter_id,
      date,
      description: f.description,
      activity_code: f.activity_code,
      billing_status: f.billing_status,
      rate: f.rate,
      rate_touched: f.rate !== '',
      matter_phase_id: f.matter_phase_id,
      start_time: startTime,
      start_time_touched: true
    }
    const body = {
      matter_id: f.matter_id,
      date,
      hours: String(hours),
      description: f.description,
      activity_code: f.activity_code,
      matter_phase_id: f.matter_phase_id ?? null,
      post_immediately: willPost,
      client_ref: manualRefRef.current,
      start_time: startTime || null
    }
    if (BILLING_STATUSES.includes(f.billing_status)) body.billing_status = f.billing_status
    if (f.rate !== '' && f.rate != null) body.rate = f.rate
    if (f.user_id) body.user_id = f.user_id
    let saved
    let isResume = false
    try {
      const res = await api.post('/api/time-entries', body)
      const created = res?.data || {}
      if (created.replayed === true) {
        saved = await reconcileReplay({ slot: pseudo, row: created, hours, willPost, mark: () => { isResume = true; return null } })
      } else {
        saved = { id: created.id ?? created.entry?.id ?? null, status: created.status, review_status: created.review_status, updated_at: created.updated_at ?? null }
      }
    } catch (err) {
      if (mountedRef.current) setError(saveFailureMessage(err, isResume))
      return
    }
    broadcastTimerEvent('saved')
    if (!mountedRef.current) return
    afterSaved({
      saved,
      willPost,
      isResume: false,
      hours,
      seconds,
      keepMatterId: f.matter_id,
      keepRate: rateTouchedRef.current ? f.rate : '',
      snapshot: {
        matter_id: f.matter_id,
        matter_title: picked?.title || '',
        description: f.description,
        activity_code: f.activity_code,
        billing_status: f.billing_status,
        rate: f.rate,
        user_id: f.user_id,
        matter_phase_id: f.matter_phase_id ?? null,
        date,
        start_time: startTime
      }
    })
  }

  const handleSave = async (postFlag) => {
    const willPost = postFlag === true
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    clearAutoClose()
    setError('')
    setSuccess('')
    setNotice('')
    try {
      const k = keyRef.current
      if (!k || !rowRef.current) { await saveManual(willPost); return }
      // Flush this form's pending merges, adopt an old-code slot, then claim.
      const flushed = flushRef.current()
      if (!flushed.ok && flushed.reason === 'claimed') { setError(MESSAGES.claimed); return }
      // Only an old-code slot waits (for adoption, maybe a confirm): a v2
      // slot is claimed synchronously, so a refused save is over before the
      // next click can arrive.
      const cls = store.classify(k)
      if (cls && cls.cls !== 'v2') {
        const adopted = await ensureAdopted(k)
        if (!adopted.ok) { showRefusal(adopted); return }
      }
      const formMatterId = formRef.current.matter_id
      let claim = store.claimForSave(k, { formMatterId })
      if (!claim.ok && claim.reason === 'confirm-owner') {
        const yes = await ask({
          type: 'confirm',
          message: MESSAGES.confirmOwnerAsk(user?.name || 'you'),
          confirmLabel: 'Save as mine'
        })
        if (yes !== true) { setError(MESSAGES.confirmOwner); return }
        claim = store.claimForSave(k, { formMatterId, ownershipConfirmed: true })
      }
      if (!claim.ok) { showRefusal(claim); refreshRef.current(true); return }
      await sendClaim(k, claim, willPost)
    } finally {
      savingRef.current = false
      if (mountedRef.current) setSaving(false)
    }
  }

  // Form submit (Save Entry, Enter) and Save & Post (requestSubmit, so HTML5
  // validation runs; the flag rides on the form's dataset because React
  // batching would lose a state update).
  const handleSubmit = (e) => {
    e.preventDefault()
    const willPost = e.currentTarget?.dataset?.postImmediately === 'true'
    if (e.currentTarget?.dataset) delete e.currentTarget.dataset.postImmediately
    handleSave(willPost)
  }

  // "▶ Continue this entry": the canonical resume slot for the entry just
  // saved, PAUSED with the exact seconds (ran:false), so the next Save PUTs
  // instead of POSTing a duplicate.
  const handleContinueLastSave = () => {
    const entry = justSavedEntry
    if (!entry) return
    clearAutoClose()
    setError('')
    // The exact seconds when they bill what was saved; otherwise the saved
    // hours.  After a save of TYPED hours the clock holds (typed − 0.1) h
    // (setTypedHours), so seeding it would show — and re-save — 0.1 h less
    // than the entry holds.
    const savedHours = Number(entry.hours)
    const actual = Number(entry.actualSeconds)
    const exact = Number.isFinite(actual) && actual > 0 &&
      (!(savedHours > 0) || sameTenth(billableHoursFromSeconds(actual), savedHours))
    const seedSeconds = exact ? actual : hoursToSeconds(entry.hours)
    const res = store.seedResume(entry.id, {
      entry,
      seedSeconds,
      entryUpdatedAt: entry.updated_at ?? null,
      date: entry.date
    })
    if (!res.ok) { showRefusal(res); return }
    setSuccess('')
    setJustSavedEntry(null)
    bind(res.key)
  }

  // Feedback #833 — post the just-saved entry straight from the banner.
  const handlePostJustSaved = async () => {
    if (!justSavedEntry || postingJustSaved) return
    clearAutoClose()
    setPostingJustSaved(true)
    setError('')
    try {
      const posted = await postTimeEntry(api, justSavedEntry.id)
      if (!mountedRef.current) return
      setJustSavedEntry(e => (e ? { ...e, status: posted.status } : e))
      setSuccess(postedMessage(posted.review_status))
      broadcastTimerEvent('saved')
    } catch (err) {
      if (mountedRef.current) setError(errorText(err, 'Could not post the entry'))
    } finally {
      if (mountedRef.current) setPostingJustSaved(false)
    }
  }

  const handleDoneAfterSave = () => {
    clearAutoClose()
    setJustSavedEntry(null)
    setSuccess('')
    if (standalone) closeWindow()
  }

  // Discard (bound): confirm, then remove the slot.  Cancel (unbound):
  // clear the form and close, as before.
  const handleDiscard = async () => {
    clearAutoClose()
    const k = keyRef.current
    const r = rowRef.current
    if (!k || !r) {
      resetUnboundChoices()
      manualRefRef.current = newUuid()
      setForm(blankForm(false))
      setJustSavedEntry(null)
      setError('')
      setSuccess('')
      setNotice('')
      closeWindow()
      return
    }
    if (r.cls === 'v1-held') { setError(MESSAGES.discardInItsWindow); return }
    // A timer old code may still run in another tab or window (v1-unknown):
    // ONE confirm that says so as well — not the adopt confirm and then the
    // discard confirm.  Yes discards it here (force: the person has answered
    // the adopt question); Cancel writes nothing.
    const oldElsewhere = r.cls === 'v1-unknown' && await store.adoptNeedsConfirm(k)
    if (!mountedRef.current || keyRef.current !== k) return
    const what = { elapsed: formatBarElapsed(r.elapsed), matter: r.matterTitle, isResume: r.isResume }
    const yes = await ask({
      type: 'confirm',
      danger: true,
      confirmLabel: 'Discard',
      message: oldElsewhere ? MESSAGES.discardOldConfirm(what) : MESSAGES.discardConfirm(what)
    })
    if (yes !== true || !mountedRef.current) return
    if (keyRef.current !== k) return
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null }
    pendingRef.current = {}
    expectRemovalRef.current = k
    // Still the adopt fallback below for a timer old code rewrote AFTER this
    // click (it read as v2 then): that confirm is the first about it.
    let res = store.discard(k, { force: r.cls === 'v1-unknown' })
    if (!res.ok && res.reason === 'needs-adopt') {
      const a = await store.adoptV1(k, { confirm })
      res = a.ok ? store.discard(k) : a
    }
    if (!res.ok) {
      expectRemovalRef.current = null
      showRefusal(res)
      refreshRef.current(true)
      return
    }
    goIdle({ notify: true })
    setForm(blankForm(false))
    setJustSavedEntry(null)
    setError('')
    setSuccess('')
    setNotice('')
    closeWindow()
  }

  // ── keyboard: only the visible in-page popup (shortcutsEnabled) ──────
  const formElRef = useRef(null)
  const shortcutRef = useRef(null)
  shortcutRef.current = {
    toggle: () => { handleTimerToggle() },
    save: () => {
      const el = formElRef.current
      if (el && typeof el.requestSubmit === 'function') el.requestSubmit()
      else handleSave(false)
    }
  }
  useEffect(() => {
    if (!shortcutsEnabled) return undefined
    const onKey = (e) => {
      if (e.defaultPrevented) return
      const t = e.target
      const tag = String(t?.tagName || '').toLowerCase()
      const editable = tag === 'input' || tag === 'textarea' || tag === 'select' || !!t?.isContentEditable
      const role = (t && typeof t.getAttribute === 'function' && t.getAttribute('role')) || ''
      const actionable = tag === 'button' || tag === 'a' || tag === 'summary' || role === 'button'
      if (editable || actionable) return
      if ((e.key === ' ' || e.code === 'Space') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        shortcutRef.current.toggle()
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        shortcutRef.current.save()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [shortcutsEnabled])

  // ── render ───────────────────────────────────────────────────────────
  const selectedId = str(form.matter_id)
  // The entry's attorney (on-behalf pick, else me): a Personal matter is
  // offered only to its own person (lib/timeEntryMatterOptions.js).
  const attorneyId = entryAttorneyId(form.user_id, user?.id)
  const matterOptions = useMemo(() => {
    const opts = []
    for (const { matter: m, inactive } of timeEntryMatterChoices(matters, { attorneyId, selectedId })) {
      const hasClient = !!(m.first_name || m.last_name || m.company_name)
      const client = hasClient ? ` — ${displayClientForMatterRow(m)}` : ''
      opts.push({ id: String(m.id), label: `${str(m.title)}${client}${inactive ? ' (inactive)' : ''}` })
    }
    // A matter the list does not carry (not loaded yet, or not visible to
    // this list), or one the effect below is about to clear: keep it
    // selectable, or the <select> would render blank.
    if (selectedId && !opts.some(o => o.id === selectedId)) {
      const carried = matters.find(m => String(m.id) === selectedId)
      opts.unshift({ id: selectedId, label: str(carried?.title) || rowMatterTitle || `Matter #${selectedId}` })
    }
    return opts
  }, [matters, selectedId, rowMatterTitle, attorneyId])

  // Someone else's Personal matter on this entry (the attorney changed, or a
  // prefill / older timer carried it): clear it and say so.  Never on a
  // locked matter (resume / uncertain save), a claimed or old-code slot, or
  // mid-save; a bound slot is cleared through the store like a matter pick.
  const rowCls = row ? row.cls : ''
  const rowClaimed = !!(row && row.claimed)
  useEffect(() => {
    if (matterLocked || savingRef.current) return
    const foreign = foreignPersonalMatter(matters, selectedId, attorneyId)
    if (!foreign) return
    const k = keyRef.current
    const r = rowRef.current
    if (k && r) {
      if (r.cls !== 'v2' || r.claimed) return
      const res = store.setMatter(k, { matter_id: '', matter_title: '' })
      if (!res.ok) { showRefusal(res); return }
      refreshRef.current(true)
    } else {
      setForm(f => {
        if (String(f.matter_id) !== String(foreign.id)) return f
        const next = { ...f, matter_id: '', matter_phase_id: null }
        if (!billingTouchedRef.current) next.billing_status = deriveDefaultBillingStatus(null)
        if (!rateTouchedRef.current) next.rate = ''
        return next
      })
    }
    setNotice(foreignPersonalNote(foreign))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matters, selectedId, attorneyId, matterLocked, rowCls, rowClaimed, store])

  const attorneyOptions = useMemo(() => {
    const list = teamMembers
      .filter(u => String(u.id) !== String(user?.id))
      .slice()
      .sort((a, b) => str(a.name).localeCompare(str(b.name), undefined, { sensitivity: 'base' }))
      .map(u => ({ id: String(u.id), label: u.name || u.email || `User #${u.id}` }))
    const sel = str(form.user_id)
    if (sel && !list.some(o => o.id === sel)) list.unshift({ id: sel, label: `User #${sel}` })
    return list
  }, [teamMembers, user?.id, form.user_id])
  const showAttorney = (canViewAll && teamMembers.length > 0) || (bound && !!form.user_id)

  const lockHint = resumeEntryId ? MESSAGES.resumeLocked : (postUncertain ? MESSAGES.postUncertain : '')
  const timingLabel = bound ? (row.matterTitle || NO_MATTER_LABEL) : ''
  const discardLabel = bound ? 'Discard' : 'Cancel'

  return (
    <div className="p-5">
      {/* What is being timed, while it ticks (the select shows it too). */}
      {isTicking && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 mb-3 text-center">
          <p className="text-xs text-green-700">⏱ Timing: <strong>{timingLabel}</strong></p>
        </div>
      )}
      {storageFailed && (
        <div role="alert" className="bg-red-50 text-red-700 border border-red-200 rounded-lg p-2 mb-3 text-xs font-semibold">
          {MESSAGES.storage}
        </div>
      )}
      {error && <div role="alert" className="bg-red-50 text-red-600 rounded-lg p-2 mb-3 text-xs">{error}</div>}
      {notice && <div role="status" className="bg-amber-50 text-amber-800 rounded-lg p-2 mb-3 text-xs">{notice}</div>}
      {success && !justSavedEntry && <div className="bg-green-50 text-green-600 rounded-lg p-2 mb-3 text-xs">{success}</div>}
      {/* "Continue this entry" banner — the accidental-save recovery (Steve
          #74).  Re-opens the entry PAUSED on its canonical resume slot; the
          next Save updates the same entry rather than creating a duplicate. */}
      {justSavedEntry && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <p className="text-xs font-semibold text-green-800">✓ {success || 'Time entry saved'}</p>
          <p className="text-xs text-green-700 mt-1">
            <strong>{(parseFloat(justSavedEntry.hours) || 0).toFixed(2)} hrs</strong>
            {justSavedEntry.matter_title && <> on <strong>{justSavedEntry.matter_title}</strong></>}
            {' '}— accidentally hit Save? Reopen it below.
          </p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <button type="button" onClick={handleContinueLastSave}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-xs font-medium">
              ▶ Continue this entry
            </button>
            {justSavedEntry.status === 'unposted' && (
              <button type="button" onClick={handlePostJustSaved} disabled={postingJustSaved}
                title="Post this entry now (locks it for billing)"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3 py-1.5 rounded text-xs font-medium">
                {postingJustSaved ? 'Posting…' : 'Post'}
              </button>
            )}
            <button type="button" onClick={handleDoneAfterSave}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-1.5 rounded text-xs font-medium">
              ✓ Done
            </button>
          </div>
        </div>
      )}
      {heldElsewhereOld && <div role="status" className="bg-amber-50 text-amber-800 rounded-lg p-2 mb-3 text-xs">{MESSAGES.held}</div>}
      {resumeEntryId && <div className="bg-purple-50 text-purple-700 rounded-lg p-2 mb-3 text-xs">▶ Resuming an existing entry — the clock continues from its logged time. Save Entry updates its hours; Save &amp; Post updates them and posts it.</div>}
      <form ref={formElRef} onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor={fid('matter')} className="block text-xs font-medium text-gray-700 mb-1">Matter</label>
          <select id={fid('matter')} value={selectedId}
            onChange={e => { onMatterChange(e.target.value) }}
            disabled={matterLocked}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60">
            <option value="">Select matter...</option>
            {matterOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          {lockHint && <p className="text-xs text-amber-700 mt-1">{lockHint}</p>}
        </div>
        {/* Feedback #512 — on behalf of another attorney (admins, view-all). */}
        {showAttorney && (
          <div>
            <label htmlFor={fid('attorney')} className="block text-xs font-medium text-gray-700 mb-1">
              Attorney <span className="text-xs text-gray-400 font-normal">(on behalf of)</span>
            </label>
            <select id={fid('attorney')} value={str(form.user_id)}
              onChange={e => { onAttorneyChange(e.target.value) }}
              disabled={matterLocked || !canViewAll}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60">
              <option value="">{user?.name || 'Me'} (self)</option>
              {attorneyOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        )}
        {/* Time + the START/STOP toggle get their own full-width row so the
            button always has room in a narrow pop-out. */}
        <div>
          <label htmlFor={fid('hours')} className="block text-xs font-medium text-gray-700 mb-1">Time</label>
          <div className="flex gap-2 items-stretch">
            {isTicking ? (
              <div className="flex-1 min-w-0 border border-green-300 bg-green-50 rounded-lg px-3 py-2 text-sm font-mono font-semibold text-green-700 text-center"
                aria-live="off" data-testid="timer-clock">
                {formatElapsed(row.elapsed)}
              </div>
            ) : (
              <input id={fid('hours')} type="number" step="0.01"
                value={form.hours}
                onChange={e => onHoursChange(e.target.value)}
                onBlur={onGuardedBlur}
                placeholder="0.00 hrs"
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            )}
            <button type="button" onClick={handleTimerToggle}
              title={isTicking ? 'Stop the clock (edit time, save, or restart)' : (isPaused ? 'Resume the clock from current time' : 'Start the clock')}
              style={{ minWidth: '80px' }}
              className={`flex-none px-4 py-2 rounded-lg text-white text-sm font-bold inline-flex items-center justify-center ${isTicking ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
              {isTicking ? 'STOP' : 'START'}
            </button>
          </div>
        </div>

        {/* Date + Start time, then Rate: two columns at the default pop-out
            width, stacked below 400px so the inputs are never crushed. */}
        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
          <div>
            <label htmlFor={fid('date')} className="block text-xs font-medium text-gray-700 mb-1">Date</label>
            <input id={fid('date')} type="date" value={form.date} onChange={e => onDateChange(e.target.value)}
              onBlur={onGuardedBlur}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            {/* Firm-local time of day the work began: filled in from when
                this timer first started (a resume keeps it), editable, may
                be left blank. */}
            <label htmlFor={fid('start_time')} className="block text-xs font-medium text-gray-700 mb-1">Start time</label>
            <input id={fid('start_time')} type="time" value={form.start_time || ''}
              onChange={e => onStartTimeChange(e.target.value)}
              onBlur={onGuardedBlur}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor={fid('rate')} className="block text-xs font-medium text-gray-700 mb-1">Rate ($/hr)</label>
            <input id={fid('rate')} type="number" step="0.01" min="0" value={form.rate} onChange={e => onRateChange(e.target.value)}
              onBlur={onGuardedBlur}
              placeholder={rateHint != null ? `${rateHint} (default)` : ''}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>
        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
          <div>
            <label htmlFor={fid('activity')} className="block text-xs font-medium text-gray-700 mb-1">Activity</label>
            <select id={fid('activity')} value={form.activity_code} onChange={e => editField('activity_code', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">Select...</option>
              {ACTIVITY_CODES.map(ac => <option key={ac.code} value={ac.code}>{ac.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor={fid('billing')} className="block text-xs font-medium text-gray-700 mb-1">Billing Status</label>
            <select id={fid('billing')} value={form.billing_status} onChange={e => onBillingChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="billable">Billable</option>
              <option value="non-billable">Non-Billable</option>
              <option value="no-charge-show">No Charge - Show on Bill</option>
              <option value="no-charge-hide">No Charge - Hide</option>
            </select>
          </div>
        </div>
        {/* D-D.6 — renders only when the chosen matter actually has phases. */}
        <PhasePicker
          matterId={form.matter_id}
          value={form.matter_phase_id}
          onChange={v => editField('matter_phase_id', v)}
        />
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor={fid('description')} className="block text-xs font-medium text-gray-700">Description</label>
            <NarrativeTemplatePicker
              activityCode={form.activity_code}
              value={form.description}
              onPick={(body) => editField('description', body)}
            />
          </div>
          <NarrativeAutocomplete
            value={form.description}
            onChange={(v) => editField('description', v)}
            activityCode={form.activity_code}
            textareaProps={{
              id: fid('description'),
              rows: 3,
              placeholder: 'Work performed...',
              className: 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'
            }}
          />
        </div>
        {/* Two-button save.  Save Entry saves unposted (Enter); Save & Post
            posts too.  Feedback #833 — offered on resume as well: the same
            status-free PUT, then /:id/post if still unposted. */}
        <div className="flex gap-2 flex-wrap">
          <button type="submit" disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 rounded-lg transition-colors">
            Save Entry
          </button>
          <button type="button" disabled={saving}
            onClick={(e) => {
              const el = e.currentTarget.form
              if (el && typeof el.requestSubmit === 'function') {
                el.dataset.postImmediately = 'true'
                el.requestSubmit()
              } else {
                handleSave(true)
              }
            }}
            data-testid="timer-save-and-post"
            title={resumeEntryId ? 'Save your updated hours and post this entry (locks it for billing)' : 'Save + post this entry in one step (locks it for billing)'}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-lg transition-colors">
            Save & Post
          </button>
          {!standalone && (
            <button type="button" onClick={() => { if (typeof onClose === 'function') onClose() }}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              Minimize
            </button>
          )}
          <button type="button" onClick={handleDiscard}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-red-600 transition-colors">
            {discardLabel}
          </button>
        </div>
      </form>
    </div>
  )
}
